<template>
  <div class="gap-1 rounded-lg bg-background-fg/3 p-4">
    <div class="mb-2 flex items-center justify-between">
      <button type="button" @click="decrementView">
        <IconChevronLeft />
      </button>
      <p class="font-semibold">
        {{
          props.type === "month"
            ? DateFns.format(currentMonthStart, "yyyy")
            : DateFns.format(currentMonthStart, "MMMM yyyy")
        }}
      </p>
      <button type="button" @click="incrementView">
        <IconChevronRight />
      </button>
    </div>
    <template v-if="props.type !== 'month'">
      <div class="mb-2 grid grid-rows-7 gap-1">
        <div class="grid grid-cols-7 gap-1">
          <template
            v-for="dayOfWeek in daysOfWeek"
            :key="dayOfWeek"
          >
            <div
              class="flex items-center justify-center text-sm text-background-fg/70"
            >
              {{ dayOfWeek }}
            </div>
          </template>
        </div>
        <template
          v-for="(dates, startOfWeek) in daysGroupedByWeeks"
          :key="startOfWeek"
        >
          <div class="group grid grid-cols-7 gap-1">
            <template
              v-for="date in dates"
              :key="date.toString()"
            >
              <div :class="$style.squareHack">
                <button
                  type="button"
                  :class="[
                    isDateActive(date).value
                      ? 'bg-primary text-primary-fg'
                      : 'bg-background text-background-fg hover:bg-primary/20',
                    'absolute inset-0 w-full rounded font-semibold disabled:opacity-20',
                  ]"
                  :disabled="isDateDisabled(date)"
                  @click="handleClickDate(date)"
                >
                  {{ DateFns.getDate(date) }}
                </button>
              </div>
            </template>
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="mb-2 grid grid-cols-3 grid-rows-4 gap-1">
        <template
          v-for="monthStartDate in monthsByStartDate"
          :key="monthStartDate.toString()"
        >
          <button
            type="button"
            :class="[
              isDateActive(monthStartDate).value
                ? 'bg-primary text-primary-fg'
                : 'bg-background text-background-fg hover:bg-primary/20',
              'w-24 rounded py-6 font-semibold disabled:opacity-20',
            ]"
            :disabled="isDateDisabled(monthStartDate)"
            @click="handleClickDate(monthStartDate)"
          >
            {{ DateFns.format(monthStartDate, "MMM") }}
          </button>
        </template>
      </div>
    </template>

    <div
      v-if="!disableCurrentMonth"
      class="flex items-center justify-between"
    >
      <button
        type="button"
        class="rounded bg-background px-2 py-1"
        @click="setModelToToday"
      >
        {{ setModelToTodayString }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as DateFns from "date-fns"

const props = withDefaults(
  defineProps<{
    modelValue?: string
    type: "day" | "month"
    disableCurrentMonth?: boolean
  }>(),
  { modelValue: undefined }
)
const emit = defineEmits(["update:modelValue"])

const getCurrentMonthStart = (date: Date | undefined) => {
  return date
    ? DateFns.startOfMonth(date)
    : DateFns.startOfMonth(new Date())
}

const internalModel = computed({
  get: () =>
    props.modelValue
      ? DateFns.parse(
          props.modelValue,
          "yyyy-MM-dd",
          new Date()
        )
      : undefined,
  set: (value: Date | undefined) =>
    value
      ? emit(
          "update:modelValue",
          DateFns.format(value, "yyyy-MM-dd")
        )
      : emit("update:modelValue"),
})

const currentMonthStart = ref(
  getCurrentMonthStart(internalModel.value)
)
const decrementView = () => {
  currentMonthStart.value =
    props.type === "month"
      ? DateFns.addYears(currentMonthStart.value, -1)
      : DateFns.addMonths(currentMonthStart.value, -1)
}

const incrementView = () => {
  currentMonthStart.value =
    props.type === "month"
      ? DateFns.addYears(currentMonthStart.value, 1)
      : DateFns.addMonths(currentMonthStart.value, 1)
}

const visibleDates = computed(() =>
  DateFns.eachDayOfInterval({
    start: DateFns.startOfISOWeek(currentMonthStart.value),
    end: DateFns.endOfISOWeek(
      DateFns.endOfMonth(currentMonthStart.value)
    ),
  })
)

const handleClickDate = (date: Date) => {
  internalModel.value = date
}

const isDateActive = (date: Date) =>
  computed(() => {
    if (!internalModel.value) return false
    return props.type === "day"
      ? DateFns.isSameDay(date, internalModel.value)
      : DateFns.isSameMonth(date, internalModel.value)
  })

const setModelToToday = () => {
  internalModel.value = new Date()
}

const isDateDisabled = (date: Date) =>
  DateFns.isFuture(date) ||
  (props.type !== "month" &&
    !DateFns.isSameMonth(date, currentMonthStart.value)) ||
  (props.disableCurrentMonth && DateFns.isThisMonth(date))

const daysOfWeek = [
  "Mo",
  "Tu",
  "We",
  "Th",
  "Fr",
  "Sa",
  "Su",
]

const daysGroupedByWeeks = computed(() =>
  groupBy(visibleDates.value, (date) =>
    DateFns.format(
      DateFns.startOfISOWeek(date),
      "yyyy-MM-dd"
    )
  )
)

const monthsByStartDate = computed(() =>
  DateFns.eachMonthOfInterval({
    start: DateFns.startOfYear(currentMonthStart.value),
    end: DateFns.endOfYear(
      DateFns.endOfMonth(currentMonthStart.value)
    ),
  })
)

const setModelToTodayString = computed(
  () =>
    ({
      day: "Today",
      month: "This Month",
    })[props.type]
)
</script>

<style lang="scss" module>
.squareHack {
  position: relative;
  padding-top: 100%;
}
</style>
